import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import TextBox from 'devextreme-react/text-box';
import { AntLocalization, AntAuth } from 'ante-react-kit';
import CustomerCard from './CustomerCard';
import FilterChips from './FilterChips';
import DetailPanel from './DetailPanel';

const SEARCH_INPUT_ATTR = { autoFocus: true };

function getSys(session, key) {
    return (session && session.bus_settings && session.bus_settings[key]) ||
           AntAuth.getSessionInfo('bus_settings')?.[key];
}

export default function SearchView({
    session, query, onQueryChange, rows, loading, loadMore, totalCount,
    activeFilters, onToggleFilter, onPickCard, onConfirmSelect, previewRow, onGoCreate,
    filterSources
}) {
    const bodyRef = useRef(null);
    const searchBoxRef = useRef(null);
    const splitRef = useRef(null);
    const dragRef = useRef(null);
    const [paneWidth, setPaneWidth] = useState(null); // null = dùng default (44%)

    const onSplitDown = useCallback((e) => {
        if (!splitRef.current) return;
        const rect = splitRef.current.getBoundingClientRect();
        dragRef.current = { startX: e.clientX, startWidth: paneWidth || Math.round(rect.width * 0.44), totalWidth: rect.width };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [paneWidth]);

    useEffect(() => {
        const onMove = (e) => {
            if (!dragRef.current) return;
            const { startX, startWidth, totalWidth } = dragRef.current;
            const delta = e.clientX - startX;
            const next = Math.max(280, Math.min(totalWidth - 320, startWidth + delta));
            setPaneWidth(next);
        };
        const onUp = () => {
            if (!dragRef.current) return;
            dragRef.current = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, []);

    useEffect(() => {
        const inst = searchBoxRef.current && searchBoxRef.current.instance;
        if (inst) {
            const t = setTimeout(() => { try { inst.focus(); } catch (e) { /* ignore */ } }, 50);
            return () => clearTimeout(t);
        }
    }, []);

    const groups = useMemo(() => {
        const arr = [];
        if (filterSources.tags && filterSources.tags.length > 0) {
            arr.push({ key: 'tags', items: filterSources.tags.map((t) => ({ value: t, label: '#' + t })) });
        }
        if (getSys(session, 'use_Module_CRM') && filterSources.collections && filterSources.collections.length > 0) {
            arr.push({ key: 'entity_collections', items: filterSources.collections.map((c) => ({ value: c.code, label: c.name || c.code })) });
        }
        if (getSys(session, 'use_Module_Loyalty') && filterSources.loyaltyLevels && filterSources.loyaltyLevels.length > 0) {
            arr.push({ key: 'loyalty_level', items: filterSources.loyaltyLevels.map((l) => ({ value: l.code, label: l.name || l.code })) });
        }
        return arr;
    }, [session, filterSources]);

    useEffect(() => {
        const marker = bodyRef.current;
        if (!marker) return;
        const scrollEl = marker.closest('.asp-shell__body');
        if (!scrollEl) return;
        const onScroll = () => {
            if (scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 60) {
                if (rows.length < totalCount && !loading) loadMore();
            }
        };
        scrollEl.addEventListener('scroll', onScroll);
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, [rows.length, totalCount, loading, loadMore]);

    const empty = !loading && rows.length === 0 && !!query;

    const hasPreview = !!previewRow;

    const paneStyle = (hasPreview && paneWidth) ? { flex: '0 0 ' + paneWidth + 'px', maxWidth: paneWidth + 'px' } : undefined;

    return (
        <div className={'apk-split' + (hasPreview ? ' apk-split--preview' : '')} ref={splitRef}>
            {/* Cột trái: tìm kiếm + danh sách card */}
            <div className="apk-list-pane" style={paneStyle}>
                <div className="apk-search-header">
                    <div className="apk-search-row">
                        <div className="apk-search-row__input">
                            <TextBox
                                ref={searchBoxRef}
                                value={query}
                                valueChangeEvent="input keyup change"
                                onValueChanged={(e) => onQueryChange(e.value || '')}
                                placeholder={AntLocalization.translateText({ vi: 'Tìm theo tên, SĐT, mã...', en: 'Search by name, phone, code...' })}
                                showClearButton={true}
                                inputAttr={SEARCH_INPUT_ATTR}
                                onEnterKey={() => {
                                    if (empty) onGoCreate(query);
                                    else if (rows.length === 1) onPickCard(rows[0]);
                                }}
                            />
                        </div>
                        <button
                            type="button"
                            className="apk-create-btn"
                            onClick={() => onGoCreate(query)}
                            title={AntLocalization.translateText({ vi: 'Tạo khách hàng mới', en: 'Create new customer' })}
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                <path d="M7 1.5v11M1.5 7h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                            <span>{AntLocalization.translateText({ vi: 'Tạo mới', en: 'New' })}</span>
                        </button>
                    </div>
                    <FilterChips groups={groups} active={activeFilters} onToggle={onToggleFilter} />
                </div>
                <div className="apk-list-body" ref={bodyRef}>
                    {loading && rows.length === 0 && (
                        <>
                            <div className="apk-skeleton" /><div className="apk-skeleton" />
                            <div className="apk-skeleton" /><div className="apk-skeleton" />
                        </>
                    )}
                    {empty && (
                        <div className="apk-empty">
                            <div className="apk-empty__text">
                                {AntLocalization.translateText({ vi: 'Không tìm thấy khách hàng', en: 'No customer found' })}
                                {' '}"{query}"
                            </div>
                            <button className="apk-empty__cta" onClick={() => onGoCreate(query)}>
                                {AntLocalization.translateText({ vi: '+ Tạo mới', en: '+ Create new' })} "{query}"
                            </button>
                        </div>
                    )}
                    {rows.map((r) => (
                        <CustomerCard
                            key={r.id}
                            row={r}
                            isActive={previewRow && previewRow.id === r.id}
                            onClick={onPickCard}
                        />
                    ))}
                    {loading && rows.length > 0 && <div className="apk-loading-more">…</div>}
                </div>
            </div>

            {/* Splitter — kéo để thay đổi tỉ lệ list / detail */}
            {hasPreview && (
                <div
                    className="apk-splitter"
                    onMouseDown={onSplitDown}
                    onDoubleClick={() => setPaneWidth(null)}
                    title={AntLocalization.translateText({ vi: 'Kéo để thay đổi kích thước · Nhấp đúp để reset', en: 'Drag to resize · Double-click to reset' })}
                >
                    <span className="apk-splitter__grip" />
                </div>
            )}

            {/* Cột phải: chi tiết KH đang preview — chỉ hiện khi có row */}
            {hasPreview && <DetailPanel row={previewRow} onConfirm={onConfirmSelect} />}
        </div>
    );
}
